import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Chess } from 'chess.js';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import ChessBoardView from '@/components/chess/ChessBoardView';
import PuzzleActivityWizard from '@/components/studies/PuzzleActivityWizard';
import CaptureActivityBuilder from '@/components/studies/CaptureActivityBuilder';
import CaptureActivityPlayer from '@/components/studies/CaptureActivityPlayer';
import PlayComputerPlayer from '@/components/studies/PlayComputerPlayer';
import LavaFloorEditor from '@/components/studies/LavaFloorEditor';
import CreateQuizDialog from '@/components/studies/CreateQuizDialog';
import MultipleChoiceEditor from '@/components/studies/MultipleChoiceEditor';
import ExplanationViewer from '@/components/studies/ExplanationViewer';
import ExplanationBuilder from '@/components/studies/ExplanationBuilder';
import { toast } from 'sonner';
import { ArrowLeft, Plus, ArrowUp, ArrowDown, MoreVertical, Trash2, Pencil, GripVertical, Video, Swords, Puzzle, ListChecks, BookOpen, Bot, Crosshair, Play, CheckCircle2, Flame, Copy } from 'lucide-react';

const START = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
const EMPTY_BOARD = '8/8/8/8/8/8/8/8 w - - 0 1';
const FILES_STR = 'abcdefgh';

// Fase PC-3: parser FEN minimalista tolerante (funciona con posiciones que
// chess.js rechaza, p.ej. sin reyes). Solo se usa dentro del EDITOR de posición.
// La validación final antes de guardar sigue siendo `new Chess(fen)`.
function fenToMap(fen) {
  try {
    const [pieces] = String(fen).split(' ');
    const ranks = pieces.split('/');
    const map = {};
    for (let r = 0; r < 8; r++) {
      let f = 0;
      for (const ch of ranks[r]) {
        if (/\d/.test(ch)) f += parseInt(ch, 10);
        else { map[FILES_STR[f] + (8 - r)] = ch; f++; }
      }
    }
    return map;
  } catch { return {}; }
}
function mapToFen(map, turn = 'w') {
  const ranks = [];
  for (let r = 8; r >= 1; r--) {
    let row = '', empty = 0;
    for (let f = 0; f < 8; f++) {
      const sq = FILES_STR[f] + r;
      const p = map[sq];
      if (p) { if (empty > 0) { row += empty; empty = 0; } row += p; }
      else empty++;
    }
    if (empty > 0) row += empty;
    ranks.push(row);
  }
  return `${ranks.join('/')} ${turn} - - 0 1`;
}
const fenTurn = (fen) => (String(fen).split(' ')[1] === 'b' ? 'b' : 'w');
const PALETTE = [
  { key:'K', label:'♔' }, { key:'Q', label:'♕' }, { key:'R', label:'♖' }, { key:'B', label:'♗' }, { key:'N', label:'♘' }, { key:'P', label:'♙' },
  { key:'k', label:'♚' }, { key:'q', label:'♛' }, { key:'r', label:'♜' }, { key:'b', label:'♝' }, { key:'n', label:'♞' }, { key:'p', label:'♟' },
];
const TYPES = [
  { key:'capture',         label:'Capture Activity',        icon:Crosshair,  desc:'El alumno debe capturar una pieza o varias' },
  { key:'puzzle',          label:'Puzzles/Tactics',         icon:Puzzle,     desc:'Resolver una táctica con la jugada correcta' },
  { key:'multiple_choice', label:'Multiple Choice Activites', icon:ListChecks, desc:'Pregunta con opciones, una correcta' },
  { key:'explanation',     label:'Explanation Activity',     icon:BookOpen,   desc:'Explicación con tablero (solo lectura)' },
  { key:'play_computer',   label:'Play With Computer',       icon:Bot,        desc:'Jugar vs el motor desde una posición' },
  { key:'interactive_lessons', label:'Lecciones Interactivas', icon:Play, isNew:true, desc:'Explorar y crear minijuegos de ajedrez' },
];
const TYPE_LABEL = { capture:'Capture Activity', puzzle:'Puzzle/Tactics Activity', multiple_choice:'Multiple Choice Activity', explanation:'Explanation Activity', play_computer:'Play with Computer Activity', video:'Video Activity', lava_floor:'El piso es lava' };
const blankForm = (type) => ({
  type, title:'', points:50, max_attempts:3, time_seconds:120, fen:START,
  solution_moves:'', hint:'',
  capture_mode:'piece', target_square:'', target_count:1,
  question:'', options:['',''], correct_index:0,
  pgn:'', text:'',
  goal:'win', student_side:'white', engine_level:5, wrong_penalty:'none',
  video_url:'', description:'',
});

export default function StudyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isOwnerAdmin = ['super_admin','school_admin'].includes(user?.role);
  const base = isOwnerAdmin ? '/admin/studies' : '/teacher/studies';

  const [study, setStudy] = useState(null);
  const [sortBy, setSortBy] = useState('custom');
  const [wizard, setWizard] = useState(null); // 'pick' | {type, editingId?}
  const [interactiveLessonsOpen, setInteractiveLessonsOpen] = useState(false);
  const [lavaEditorOpen, setLavaEditorOpen] = useState(false);
  const [lavaEditingActivity, setLavaEditingActivity] = useState(null);
  const [lavaStartPresentation, setLavaStartPresentation] = useState(false);
  const [puzzleWizard, setPuzzleWizard] = useState(null); // {editingId, activity}
  const [captureBuilder, setCaptureBuilder] = useState(null); // {editingId, activity}
  const [captureSaveMessage, setCaptureSaveMessage] = useState('');
  const captureSaveTimer = React.useRef(null);
  const [capturePlayer, setCapturePlayer] = useState(null); // activity being played
  const [playComputer, setPlayComputer] = useState(null); // Fase PC-1: play_computer runner
  const [explanationViewer, setExplanationViewer] = useState(null); // Explanation V2 · Etapa 1: viewer
  const [explanationBuilder, setExplanationBuilder] = useState(null); // Explanation V2 · Etapa 2: builder
  const [createQuizOpen, setCreateQuizOpen] = useState(false); // Fase Quizzes P3: dialog Crear Quiz
  const [form, setForm] = useState(blankForm('puzzle'));
  // Fase PC-3: herramienta activa del editor de posición para play_computer.
  // null = mover piezas existentes por drag · 'erase' = borrar al hacer clic
  // 'K'|'Q'|... = colocar pieza al hacer clic (mayúscula=blanca, minúscula=negra)
  const [setupTool, setSetupTool] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/studies/${id}`); setStudy(data); }
    catch { toast.error('No se pudo cargar el estudio'); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const canEdit = study && (study.owner_id === user?.id || isOwnerAdmin);
  const activities = study?.activities || [];
  // Paso 3B fix: Crear Quiz es una acción de USO del contenido (no edición).
  // El teacher con acceso de solo lectura (Study asignado/compartido) también
  // debe poder crearlo. El backend (POST /quizzes) revalida el acceso al Study.
  const canCreateQuiz = !!study && activities.length > 0 && (canEdit || user?.role === 'teacher');

  const openForm = (type, activity) => {
    if (type === 'lava_floor') {
      setWizard(null);
      setLavaStartPresentation(false);
      setLavaEditingActivity(activity || null);
      setLavaEditorOpen(true);
      return;
    }
    if (type === 'puzzle') {
      setWizard(null);
      setPuzzleWizard({ editingId: activity?.id || null, activity: activity || null });
      return;
    }
    if (type === 'capture') {
      setWizard(null);
      setCaptureBuilder({ editingId: activity?.id || null, activity: activity || null });
      return;
    }
    if (type === 'explanation') {
      setWizard(null);
      setExplanationBuilder({ editingId: activity?.id || null, activity: activity || null });
      return;
    }
    if (activity) {
      setForm({ ...blankForm(type), ...activity, solution_moves: (activity.solution_moves||[]).join(' '), options: activity.options?.length ? activity.options : ['',''] });
      setWizard({ type, editingId: activity.id });
    } else { setForm(blankForm(type)); setWizard({ type }); }
  };

  const startLavaPresentation = (activity) => {
    setWizard(null);
    setLavaStartPresentation(true);
    setLavaEditingActivity(activity);
    setLavaEditorOpen(true);
  };

  const saveActivity = async () => {
    if (!form.title.trim()) { toast.error('Título requerido'); return; }
    const payload = {
      type: form.type, title: form.title, points: Number(form.points)||0,
      max_attempts: Number(form.max_attempts)||1, time_seconds: Number(form.time_seconds)||60,
    };
    if (['capture','puzzle','multiple_choice','explanation','play_computer'].includes(form.type)) {
      if (form.type !== 'explanation' || form.fen) {
        try { new Chess(form.fen); } catch { toast.error('FEN inválido'); return; }
        payload.fen = form.fen;
      }
    }
    if (form.type === 'puzzle') { payload.solution_moves = form.solution_moves.split(/[\s,]+/).filter(Boolean); payload.hint = form.hint; }
    if (form.type === 'capture') { payload.capture_mode = form.capture_mode; payload.target_square = form.target_square; payload.target_count = Number(form.target_count)||1; }
    if (form.type === 'multiple_choice') { payload.question = form.question; payload.options = form.options.filter(o=>o.trim()); payload.correct_index = Number(form.correct_index)||0; }
    if (form.type === 'explanation') { payload.pgn = form.pgn; payload.text = form.text; }
    if (form.type === 'play_computer') { payload.goal = form.goal; payload.student_side = form.student_side; payload.engine_level = Number(form.engine_level)||5; payload.wrong_penalty = form.wrong_penalty; }
    if (form.type === 'video') { payload.video_url = form.video_url; payload.description = form.description; }
    try {
      if (wizard.editingId) await api.patch(`/studies/${id}/activities/${wizard.editingId}`, payload);
      else await api.post(`/studies/${id}/activities`, payload);
      toast.success(wizard.editingId ? 'Actividad actualizada' : 'Actividad agregada');
      setWizard(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const saveExplanation = async (payload, editingId) => {
    try {
      if (editingId) await api.patch(`/studies/${id}/activities/${editingId}`, payload);
      else await api.post(`/studies/${id}/activities`, payload);
      toast.success(editingId ? 'Explicación actualizada' : 'Explicación creada');
      setExplanationBuilder(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const savePuzzle = async (payload) => {
    try {
      if (puzzleWizard.editingId) await api.patch(`/studies/${id}/activities/${puzzleWizard.editingId}`, payload);
      else await api.post(`/studies/${id}/activities`, payload);
      toast.success(puzzleWizard.editingId ? 'Actividad actualizada' : 'Actividad agregada');
      setPuzzleWizard(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const saveCapture = async (payload, editingId) => {
    try {
      if (editingId) await api.patch(`/studies/${id}/activities/${editingId}`, payload);
      else await api.post(`/studies/${id}/activities`, payload);
      setCaptureSaveMessage(editingId ? 'Actividad actualizada' : 'Actividad agregada');
      if (captureSaveTimer.current) clearTimeout(captureSaveTimer.current);
      captureSaveTimer.current = setTimeout(() => setCaptureSaveMessage(''), 5000);
      setCaptureBuilder(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const savePuzzleBulk = async (gameIds, cfg) => {
    try {
      const { data } = await api.post(`/studies/${id}/activities/from-games`, { game_ids: gameIds, ...cfg });
      toast.success(`${data.created} ejercicios agregados`);
      setPuzzleWizard(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const delActivity = async (aid) => {
    if (!window.confirm('¿Borrar esta actividad?')) return;
    await api.delete(`/studies/${id}/activities/${aid}`);
    setCaptureSaveMessage('Borrada');
    if (captureSaveTimer.current) clearTimeout(captureSaveTimer.current);
    captureSaveTimer.current = setTimeout(() => setCaptureSaveMessage(''), 5000);
    load();
  };
  const duplicateLavaActivity = async (activity) => {
    if (activity?.type !== 'lava_floor') return;
    const payload = {
      type: 'lava_floor',
      title: `${activity.title || 'El piso es lava'} (copia)`,
      points: Number(activity.points ?? 50),
      max_attempts: Number(activity.max_attempts ?? 3),
      time_seconds: Number(activity.time_seconds ?? 120),
      fen: activity.fen,
      lava_config: activity.lava_config
        ? JSON.parse(JSON.stringify(activity.lava_config))
        : { ignitedLines: [], steps: [] },
      instructions: activity.instructions || 'Llega a la casilla meta evitando todas las casillas con lava.'
    };
    try {
      await api.post(`/studies/${id}/activities`, payload);
      toast.success('Posición duplicada. Puedes editar la copia.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo duplicar la posición');
    }
  };

  const move = async (idx, dir) => {
    const ids = activities.map(a=>a.id);
    const j = idx + dir; if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    await api.post(`/studies/${id}/activities/reorder`, { order: ids }); load();
  };

  if (!study) return <div className="p-10 text-slate-500">Cargando…</div>;

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button data-testid="study-back" onClick={()=>nav(base)} className="p-2 hover:bg-slate-200 rounded-lg"><ArrowLeft className="w-4 h-4"/></button>
          <h1 className="font-display text-xl font-bold">{study.name}</h1>
        </div>
        {(canCreateQuiz || canEdit) && (
          <div className="flex items-center gap-2">
            {canCreateQuiz && (
              <Button data-testid="create-quiz-btn" onClick={()=>setCreateQuizOpen(true)} variant="outline" className="rounded-lg border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                🎯 Crear Quiz
              </Button>
            )}
            {canEdit && (
              <div className="relative flex flex-col items-end">
                <Button data-testid="add-activity-btn" onClick={()=>setWizard('pick')} className="rounded-lg bg-indigo-600 hover:bg-indigo-700 px-8">
                  <Plus className="w-4 h-4 mr-1"/> Add Activity
                </Button>
                {captureSaveMessage && (
                  <span role="status" className="absolute top-full right-0 mt-6 inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800 shadow-md whitespace-nowrap animate-pulse">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    {captureSaveMessage}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="p-6 max-w-5xl">
        <div className="flex items-center gap-2 text-indigo-600 font-semibold mb-1"><GripVertical className="w-4 h-4"/> Manage Activity Order</div>
        <p className="text-sm text-slate-500 mb-4">Usa las flechas para ordenar las actividades en un orden personalizado.</p>
        <div className="flex items-center gap-2 mb-5">
          <span className="text-sm text-slate-600">Sort by:</span>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="activities-sort" className="w-48"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="custom">Custom Order</SelectItem><SelectItem value="type">Type</SelectItem></SelectContent>
          </Select>
        </div>

        {activities.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500">
            Aún no hay actividades. {canEdit ? 'Pulsa "Add Activity" para empezar.' : ''}
          </div>
        ) : (
          <div className="space-y-4">
            {(sortBy==='type' ? [...activities].sort((a,b)=>a.type.localeCompare(b.type)) : activities).map((a, i) => (
              <div key={a.id} data-testid={`activity-${a.id}`} className="flex gap-3">
                <div className="flex flex-col items-center gap-1 pt-1">
                  <div className="w-8 h-8 rounded-full bg-sky-500 text-white grid place-items-center font-bold text-sm">{i+1}</div>
                  {canEdit && sortBy==='custom' && (
                    <>
                      <button data-testid={`act-up-${a.id}`} onClick={()=>move(i,-1)} disabled={i===0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUp className="w-4 h-4"/></button>
                      <button data-testid={`act-down-${a.id}`} onClick={()=>move(i,1)} disabled={i===activities.length-1} className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDown className="w-4 h-4"/></button>
                    </>
                  )}
                </div>
                <div className="flex-1 rounded-2xl bg-slate-50 border border-slate-200 p-3 flex gap-4">
                  <div className="w-40 shrink-0">
                    {a.type === 'video'
                      ? <div className="w-40 h-40 rounded-lg bg-slate-900 grid place-items-center text-white"><Video className="w-8 h-8"/></div>
                      : <ChessBoardView fen={a.fen || START} boardWidth={160} arePiecesDraggable={false}/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-500">{TYPE_LABEL[a.type] || a.type}</div>
                    <div className="font-display text-lg font-bold flex items-center gap-2">
                      {a.title}
                      {canEdit && <button data-testid={`act-edit-${a.id}`} onClick={()=>openForm(a.type, a)}><Pencil className="w-4 h-4 text-indigo-500"/></button>}
                      {a.type === 'lava_floor' && (
                        <button
                          data-testid={`act-present-lava-${a.id}`}
                          onClick={() => startLavaPresentation(a)}
                          title="Iniciar explicación"
                          className="ml-1 inline-flex items-center gap-1 rounded-md bg-orange-500 px-2 py-1 text-xs font-semibold text-white hover:bg-orange-600"
                        ><Play className="h-3.5 w-3.5" /> Iniciar explicación</button>
                      )}
                      {a.type === 'capture' && a.capture_config && (
                        <button
                          data-testid={`act-play-${a.id}`}
                          onClick={()=>setCapturePlayer(a)}
                          title="Jugar / Probar"
                          className="ml-1 inline-flex items-center gap-1 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold px-2 py-1"
                        ><Play className="w-3.5 h-3.5"/> Jugar</button>
                      )}
                      {a.type === 'play_computer' && a.fen && (
                        <button
                          data-testid={`act-play-pc-${a.id}`}
                          onClick={()=>setPlayComputer(a)}
                          title="Jugar contra el motor"
                          className="ml-1 inline-flex items-center gap-1 rounded-md bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold px-2 py-1"
                        ><Play className="w-3.5 h-3.5"/> Jugar</button>
                      )}
                      {a.type === 'explanation' && (
                        <button
                          data-testid={`act-view-explanation-${a.id}`}
                          onClick={()=>setExplanationViewer(a)}
                          title="Ver explicación"
                          className="ml-1 inline-flex items-center gap-1 rounded-md bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold px-2 py-1"
                        ><Play className="w-3.5 h-3.5"/> Ver</button>
                      )}
                    </div>
                    <div className="text-sm text-slate-600 mt-2 space-y-0.5">
                      <div>Score: {a.points}</div>
                      <div>Max attempts: {a.max_attempts}</div>
                      {a.type === 'play_computer' && <><div>Goal: {a.goal === 'draw' ? 'Draw' : 'Win'}</div><div>Student Plays: {a.student_side === 'black' ? 'Black' : 'White'}</div><div>Wrong answer: {a.wrong_penalty === 'none' ? 'No Penalty' : a.wrong_penalty}</div></>}
                      {a.type === 'puzzle' && <div>Solution: {(a.solution_moves||[]).join(' ') || '—'}</div>}
                      {a.type === 'multiple_choice' && <div>Options: {(a.options||[]).length} · Correct #{(a.correct_index??0)+1}</div>}
                      {a.type === 'capture' && <div>Goal: {a.capture_mode === 'count' ? `Capturar ${a.target_count} piezas` : `Capturar en ${a.target_square || '—'}`}</div>}
                      {a.type === 'video' && <div className="truncate">Video: {a.video_url || '—'}</div>}
                    </div>
                  </div>
                  {canEdit && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><button data-testid={`act-menu-${a.id}`} className="self-start p-1.5 hover:bg-slate-200 rounded"><MoreVertical className="w-4 h-4"/></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={()=>openForm(a.type, a)}><Pencil className="w-3.5 h-3.5 mr-2"/> Editar</DropdownMenuItem>
                        {a.type === 'lava_floor' && (
                          <DropdownMenuItem
                            data-testid={`act-duplicate-lava-${a.id}`}
                            onClick={() => duplicateLavaActivity(a)}
                          ><Copy className="w-3.5 h-3.5 mr-2"/> Duplicar posición</DropdownMenuItem>
                        )}
                        <DropdownMenuItem data-testid={`act-delete-${a.id}`} onClick={()=>delActivity(a.id)} className="text-rose-600"><Trash2 className="w-3.5 h-3.5 mr-2"/> Borrar</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Type picker */}
      <Dialog open={wizard === 'pick'} onOpenChange={(v)=>{ if(!v) setWizard(null); }}>
        <DialogContent data-testid="activity-picker" className="max-w-3xl">
          <DialogHeader><DialogTitle className="text-center text-2xl font-extrabold">What type of activity do you want to give your students?</DialogTitle>
            <DialogDescription className="text-center">Elige un tipo de actividad para agregar al estudio.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-2">
            {TYPES.map(t => (
              <button key={t.key} data-testid={`pick-${t.key}`} onClick={()=>{ if (t.key === "interactive_lessons") { setWizard(null); setInteractiveLessonsOpen(true); } else { openForm(t.key); } }}
                className="relative text-left rounded-xl bg-slate-100 hover:bg-indigo-50 hover:ring-2 hover:ring-indigo-400 p-4 h-28 flex flex-col justify-between transition-all">
                {t.isNew && <span className="absolute -top-2 -right-2 bg-rose-500 text-white text-[10px] font-bold rounded-full px-2 py-0.5">New</span>}
                <t.icon className="w-5 h-5 text-slate-600"/>
                <div className="font-semibold text-sm">{t.label}</div>
              </button>
            ))}
          </div>
          <DialogFooter className="sm:justify-center"><Button variant="outline" onClick={()=>setWizard(null)}>Back</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config form */}
      <Dialog open={!!wizard && wizard !== 'pick'} onOpenChange={(v)=>{ if(!v) setWizard(null); }}>
        <DialogContent data-testid="activity-form" className={form.type === 'multiple_choice' ? 'max-w-4xl' : 'max-w-lg'}>
          <DialogHeader><DialogTitle>{wizard?.editingId ? 'Editar' : 'Nueva'} · {TYPE_LABEL[form.type]}</DialogTitle>
            <DialogDescription>Configura la actividad para tus alumnos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            {form.type === 'multiple_choice' && <MultipleChoiceEditor form={form} setForm={setForm} />}
            {form.type !== 'multiple_choice' && (
            <div><Label>Título</Label><Input data-testid="act-title" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))}/></div>
            )}

            {form.type !== 'video' && form.type !== 'play_computer' && form.type !== 'multiple_choice' && (
              <>
                <div><Label>FEN</Label><Input data-testid="act-fen" className="font-mono text-xs" value={form.fen} onChange={e=>setForm(f=>({...f,fen:e.target.value}))}/></div>
                <div className="grid place-items-center">
                  {(() => { try { new Chess(form.fen); return <ChessBoardView fen={form.fen} boardWidth={220} arePiecesDraggable={false}/>; } catch { return <div className="text-xs text-rose-500">FEN inválido</div>; } })()}
                </div>
              </>
            )}

            {form.type === 'play_computer' && (
              <div className="space-y-2" data-testid="pc-setup">
                <Label>Editor de posición</Label>
                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" variant="outline" data-testid="pc-preset-start" onClick={()=>setForm(f=>({...f,fen:START}))}>Tablero</Button>
                  <Button size="sm" variant="outline" data-testid="pc-preset-clear" onClick={()=>setForm(f=>({...f,fen:mapToFen({}, fenTurn(f.fen))}))}>Borrar tablero</Button>
                  <Button size="sm" variant={fenTurn(form.fen)==='w'?'default':'outline'} data-testid="pc-turn-w" onClick={()=>setForm(f=>({...f,fen:mapToFen(fenToMap(f.fen),'w')}))}>Turno blancas</Button>
                  <Button size="sm" variant={fenTurn(form.fen)==='b'?'default':'outline'} data-testid="pc-turn-b" onClick={()=>setForm(f=>({...f,fen:mapToFen(fenToMap(f.fen),'b')}))}>Turno negras</Button>
                </div>
                <div className="rounded-md bg-slate-50 border border-slate-200 p-2 space-y-1.5">
                  <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Herramienta:</div>
                  <div className="flex flex-wrap gap-1">
                    <button type="button" data-testid="pc-tool-move"
                      onClick={()=>setSetupTool(null)}
                      className={`h-7 px-2 rounded text-xs font-semibold border ${setupTool===null?'bg-indigo-600 text-white border-indigo-600':'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'}`}>Mover</button>
                    <button type="button" data-testid="pc-tool-erase"
                      onClick={()=>setSetupTool('erase')}
                      className={`h-7 px-2 rounded text-xs font-semibold border ${setupTool==='erase'?'bg-rose-600 text-white border-rose-600':'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'}`}>Borrar</button>
                    {PALETTE.map(p => (
                      <button type="button" key={p.key} data-testid={`pc-tool-${p.key}`}
                        onClick={()=>setSetupTool(p.key)}
                        title={`Colocar ${p.key}`}
                        className={`h-7 w-7 rounded text-lg leading-none border ${setupTool===p.key?'bg-amber-400 border-amber-500':'bg-white border-slate-300 hover:bg-slate-100'} ${p.key===p.key.toLowerCase()?'text-slate-900':'text-slate-700'}`}>{p.label}</button>
                    ))}
                  </div>
                  <div className="text-[10px] text-slate-500">Modo <b>Mover</b>: arrastra las piezas en el tablero. Elige una pieza y haz clic en una casilla para colocarla. <b>Borrar</b>: clic en casilla para vaciarla.</div>
                </div>
                <div className="grid place-items-center">
                  <ChessBoardView
                    fen={(() => { try { new Chess(form.fen); return form.fen; } catch { return mapToFen(fenToMap(form.fen), fenTurn(form.fen)); } })()}
                    boardWidth={280}
                    arePiecesDraggable={setupTool===null}
                    onPieceDrop={(from,to)=>{
                      // Modo mover: reposiciona sin restricción de turno
                      const map = fenToMap(form.fen);
                      const piece = map[from]; if (!piece) return false;
                      delete map[from];
                      map[to] = piece;
                      setForm(f=>({...f, fen: mapToFen(map, fenTurn(f.fen))}));
                      return true;
                    }}
                    onSquareClick={(sq)=>{
                      if (setupTool === null) return; // Modo mover: no-op en clic
                      const map = fenToMap(form.fen);
                      if (setupTool === 'erase') { delete map[sq]; }
                      else { map[sq] = setupTool; }
                      setForm(f=>({...f, fen: mapToFen(map, fenTurn(f.fen))}));
                    }}
                  />
                </div>
                <div>
                  <Label className="text-[11px]">FEN (avanzado)</Label>
                  <Input data-testid="act-fen" className="font-mono text-[11px]"
                    value={form.fen} onChange={e=>setForm(f=>({...f,fen:e.target.value}))}/>
                  {(() => { try { new Chess(form.fen); return <div className="text-[10px] text-emerald-600 mt-0.5" data-testid="pc-fen-ok">FEN válido</div>; } catch { return <div className="text-[10px] text-rose-500 mt-0.5" data-testid="pc-fen-bad">FEN aún inválido — chess.js requiere ambos reyes y una posición legal antes de guardar.</div>; } })()}
                </div>
              </div>
            )}

            {form.type === 'puzzle' && (
              <>
                <div><Label>Jugadas solución (SAN)</Label><Input data-testid="act-solution" className="font-mono" value={form.solution_moves} onChange={e=>setForm(f=>({...f,solution_moves:e.target.value}))} placeholder="Ej: Qxh7+ Kxh7 Rh3#"/></div>
                <div><Label>Pista (opcional)</Label><Input data-testid="act-hint" value={form.hint} onChange={e=>setForm(f=>({...f,hint:e.target.value}))}/></div>
              </>
            )}
            {form.type === 'capture' && (
              <>
                <div><Label>Modo</Label>
                  <Select value={form.capture_mode} onValueChange={v=>setForm(f=>({...f,capture_mode:v}))}>
                    <SelectTrigger data-testid="act-capture-mode"><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="piece">Capturar pieza en casilla</SelectItem><SelectItem value="count">Capturar N piezas</SelectItem></SelectContent>
                  </Select>
                </div>
                {form.capture_mode === 'piece'
                  ? <div><Label>Casilla objetivo</Label><Input data-testid="act-target-square" value={form.target_square} onChange={e=>setForm(f=>({...f,target_square:e.target.value}))} placeholder="Ej: e5"/></div>
                  : <div><Label>Número de piezas</Label><Input data-testid="act-target-count" type="number" value={form.target_count} onChange={e=>setForm(f=>({...f,target_count:e.target.value}))}/></div>}
              </>
            )}
            {form.type === 'multiple_choice' && null}
            {form.type === 'explanation' && (
              <>
                <div><Label>PGN (opcional)</Label><Textarea data-testid="act-pgn" rows={3} className="font-mono text-xs" value={form.pgn} onChange={e=>setForm(f=>({...f,pgn:e.target.value}))}/></div>
                <div><Label>Explicación</Label><Textarea data-testid="act-text" rows={3} value={form.text} onChange={e=>setForm(f=>({...f,text:e.target.value}))}/></div>
              </>
            )}
            {form.type === 'play_computer' && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Objetivo</Label>
                  <Select value={form.goal} onValueChange={v=>setForm(f=>({...f,goal:v}))}><SelectTrigger data-testid="act-goal"><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="win">Ganar</SelectItem><SelectItem value="draw">Empatar</SelectItem></SelectContent></Select>
                </div>
                <div><Label>Alumno juega</Label>
                  <Select value={form.student_side} onValueChange={v=>setForm(f=>({...f,student_side:v}))}><SelectTrigger data-testid="act-side"><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="white">Blancas</SelectItem><SelectItem value="black">Negras</SelectItem></SelectContent></Select>
                </div>
                <div><Label>Nivel motor</Label>
                  <Select value={String(form.engine_level)} onValueChange={v=>setForm(f=>({...f,engine_level:Number(v)}))}><SelectTrigger data-testid="act-engine"><SelectValue/></SelectTrigger>
                    <SelectContent>{[0,1,3,5,8,12,15,20].map(l=><SelectItem key={l} value={String(l)}>Nivel {l}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label>Penalización</Label>
                  <Select value={form.wrong_penalty} onValueChange={v=>setForm(f=>({...f,wrong_penalty:v}))}><SelectTrigger data-testid="act-penalty"><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="none">Ninguna</SelectItem><SelectItem value="attempt">Resta intento</SelectItem><SelectItem value="points">Resta puntos</SelectItem></SelectContent></Select>
                </div>
              </div>
            )}
            {form.type === 'video' && (
              <>
                <div><Label>URL de YouTube</Label><Input data-testid="act-video-url" value={form.video_url} onChange={e=>setForm(f=>({...f,video_url:e.target.value}))} placeholder="https://youtube.com/watch?v=…"/></div>
                <div><Label>Descripción</Label><Textarea data-testid="act-video-desc" rows={2} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/></div>
              </>
            )}

            {form.type !== 'multiple_choice' && (
            <div className="grid grid-cols-3 gap-3 pt-2 border-t">
              <div><Label>Puntaje</Label><Input data-testid="act-points" type="number" value={form.points} onChange={e=>setForm(f=>({...f,points:e.target.value}))}/></div>
              <div><Label>Intentos</Label><Input data-testid="act-attempts" type="number" value={form.max_attempts} onChange={e=>setForm(f=>({...f,max_attempts:e.target.value}))}/></div>
              <div><Label>Tiempo (seg)</Label><Input data-testid="act-time" type="number" value={form.time_seconds} onChange={e=>setForm(f=>({...f,time_seconds:e.target.value}))}/></div>
            </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setWizard('pick')}>Atrás</Button>
            <Button data-testid="act-save" onClick={saveActivity} className="bg-indigo-600 hover:bg-indigo-700">{wizard?.editingId ? 'Guardar' : 'Agregar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Editor inicial de El piso es lava: aislado de las demás actividades */}
      <Dialog
        open={lavaEditorOpen}
        onOpenChange={(nextOpen) => {
          setLavaEditorOpen(nextOpen);
          if (!nextOpen) {
            setLavaEditingActivity(null);
            setLavaStartPresentation(false);
          }
        }}
      >
        <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto">
          <LavaFloorEditor
            open={lavaEditorOpen}
            studyId={id}
            activity={lavaEditingActivity}
            startInPresentation={lavaStartPresentation}
            lavaActivities={activities.filter(item => item.type === 'lava_floor')}
            onNavigateActivity={(nextActivity) => setLavaEditingActivity(nextActivity)}
            onSaved={load}
            onClose={() => {
              setLavaEditorOpen(false);
              setLavaEditingActivity(null);
              setLavaStartPresentation(false);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Biblioteca de Lecciones Interactivas · Fase 1: interfaz */}
      <Dialog
        open={interactiveLessonsOpen}
        onOpenChange={setInteractiveLessonsOpen}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold">
              Biblioteca de Lecciones Interactivas
            </DialogTitle>
            <DialogDescription>
              Explora minijuegos de ajedrez y prepara nuevas experiencias para tus alumnos.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[65vh] space-y-4 overflow-y-auto py-2">
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white">
                  <Play className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-bold text-slate-900">El piso es lava</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    Minijuego de recorrido por el tablero. El alumno deberá encontrar una ruta
                    segura y cumplir la misión indicada.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                      Minijuego
                    </span>
                    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                      En diseño
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => {
                    setInteractiveLessonsOpen(false);
                    setLavaEditingActivity(null);
                    setLavaEditorOpen(true);
                  }}
                >
                  <Play className="mr-2 h-4 w-4" />
                  Abrir editor
                </Button>
              </div>
              <p className="mt-3 rounded-lg border border-indigo-100 bg-white p-3 text-xs text-slate-500">
                El editor ya permite guardar la configuración del tablero y las líneas de lava.
                El recorrido del alumno y el registro de progreso siguen pendientes.
              </p>
            </div>

            <div className="rounded-xl border border-dashed border-slate-300 p-5 text-center">
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                <Plus className="h-5 w-5 text-slate-600" />
              </div>
              <h3 className="mt-2 font-semibold text-slate-800">Crear nueva lección</h3>
              <p className="mt-1 text-sm text-slate-500">
                Aquí podremos incorporar más minijuegos en futuras etapas.
              </p>
              <Button
                type="button"
                variant="outline"
                className="mt-3"
                onClick={() => toast.info('El creador de lecciones se implementará en la siguiente etapa.')}
              >
                <Plus className="mr-2 h-4 w-4" />
                Próximamente
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setInteractiveLessonsOpen(false)}
            >
              Cerrar biblioteca
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Puzzle wizard (2 steps: select game -> configuration) */}
      <PuzzleActivityWizard
        open={!!puzzleWizard}
        onClose={()=>setPuzzleWizard(null)}
        onSave={savePuzzle}
        onSaveBulk={savePuzzleBulk}
        initial={puzzleWizard}
      />
      {/* Capture activity builder (custom, not PGN based) */}
      <CaptureActivityBuilder
        open={!!captureBuilder}
        onClose={()=>setCaptureBuilder(null)}
        onSave={saveCapture}
        initial={captureBuilder}
      />
      {/* Capture activity player (rook mechanics for now) */}
      <CaptureActivityPlayer
        open={!!capturePlayer}
        onClose={()=>setCapturePlayer(null)}
        activity={capturePlayer}
      />
      {/* Fase PC-1: Play with Computer runner (Stockfish) */}
      <PlayComputerPlayer
        open={!!playComputer}
        onClose={()=>setPlayComputer(null)}
        activity={playComputer}
        studyId={id}
      />
      {/* Fase Quizzes · Paso 3: Crear Quiz desde este Study */}
      <CreateQuizDialog
        open={createQuizOpen}
        onClose={()=>setCreateQuizOpen(false)}
        study={study}
        onCreated={()=>{ /* No refresh Study — el quiz vive en su propia colección */ }}
      />
      {/* Explanation V2 · Etapa 1: Viewer legacy-safe */}
      <ExplanationViewer
        open={!!explanationViewer}
        activity={explanationViewer}
        onClose={()=>setExplanationViewer(null)}
      />
      {/* Explanation V2 · Etapa 2: Builder por Steps */}
      <ExplanationBuilder
        open={!!explanationBuilder}
        initial={explanationBuilder}
        onClose={()=>setExplanationBuilder(null)}
        onSave={saveExplanation}
      />
    </div>
  );
}
